import { forwardRef, useEffect, useMemo, useRef, useState } from 'react';
import type { ExtractedEvent } from '../types';
import {
  TAG_GROUPS,
  formatTag,
  sortByLabel,
  tagIcon,
  visibleChipTags,
} from '../lib/tagTaxonomy';
import { useIsAdmin } from '../lib/auth';
import { api, isAuthPending } from '../lib/api';
import { clearSwrCacheByPrefix } from '../lib/swrCache';

interface Props {
  /** The event whose tags we are rendering / editing. */
  event: ExtractedEvent;
  /**
   * Optional callback invoked with the merged event after a successful tag
   * mutation. Triggers the inline editor when present and the user is admin;
   * without it the chips stay read-only (matches non-admin / public surfaces).
   */
  onChange?: (updated: ExtractedEvent) => void;
  /**
   * Cap visible chips before showing "+N more" (read mode only). When the user
   * opens the editor, all tags are listed regardless of `max`.
   */
  max?: number;
  /** Bootstrap classes for each rendered chip. */
  chipClassName?: string;
  /** Layout class for the chip row. */
  className?: string;
  /** Aria label for the chip row container. */
  ariaLabel?: string;
}

const DEFAULT_CHIP = 'badge rounded-pill text-bg-light border small';

/** Flat list of every taxonomy slug, used to power the editor autocomplete. */
const ALL_TAXONOMY_TAGS: string[] = sortByLabel(
  Array.from(new Set(TAG_GROUPS.flatMap((g) => g.tags)))
);

/** Light slugify so free-text additions stay consistent with taxonomy slugs. */
function slugifyTag(input: string): string {
  return input
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

/**
 * Reusable tag-chip strip with optional inline edit affordance for admins.
 *
 * - Read mode (default / non-admin / no `onChange`): renders the same chip
 *   strip as before, with `+N more` collapse when `max` is set.
 * - Edit mode (admin + `onChange`): adds a small pencil button that opens
 *   an inline popover. Each tag has a × to delete; an autocomplete combobox
 *   adds new tags. Mutations are optimistic + persisted via PATCH
 *   `/api/mgmt/events/{id}` with the merged `tagsJson`.
 */
export default function EventTagChips({
  event,
  onChange,
  max,
  chipClassName,
  className,
  ariaLabel,
}: Props) {
  const isAdmin = useIsAdmin();
  const editable = isAdmin && !!onChange;
  const visibleAll = visibleChipTags(event.tags, event);
  const visible = max != null ? visibleAll.slice(0, max) : visibleAll;
  const overflow = max != null ? Math.max(0, visibleAll.length - visible.length) : 0;
  const chipClass = chipClassName ?? DEFAULT_CHIP;

  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const popoverRef = useRef<HTMLDivElement | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);

  // Close on outside click / Escape.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (
        popoverRef.current?.contains(t) ||
        triggerRef.current?.contains(t)
      ) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
        triggerRef.current?.focus();
      }
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const persist = async (nextTags: string[]) => {
    if (!onChange) return;
    setSaving(true);
    setError(null);
    // Optimistic update: hand caller a merged event right away so list
    // chips re-render immediately.
    const optimistic: ExtractedEvent = { ...event, tags: nextTags };
    onChange(optimistic);
    try {
      const updated = await api.patch<ExtractedEvent>(
        `/api/mgmt/events/${encodeURIComponent(event.rowKey)}`,
        { tagsJson: JSON.stringify(nextTags) }
      );
      // Backend returns the raw entity (tagsJson, no parsed `tags`). Re-merge
      // our parsed array so consumers get the correct shape.
      onChange({ ...event, ...updated, tags: nextTags });
      clearSwrCacheByPrefix('events:');
    } catch (err) {
      if (!isAuthPending(err)) {
        setError(err instanceof Error ? err.message : String(err));
        // Revert by re-emitting the original event.
        onChange({ ...event });
      }
    } finally {
      setSaving(false);
    }
  };

  const removeTag = (tag: string) => {
    const next = (event.tags ?? []).filter((t) => t !== tag);
    void persist(next);
  };

  const addTag = (raw: string) => {
    const slug = slugifyTag(raw);
    if (!slug) return;
    const current = event.tags ?? [];
    if (current.includes(slug)) return;
    void persist([...current, slug]);
  };

  // Read-only render path (preserves prior visual exactly).
  if (!editable) {
    if (visible.length === 0 && overflow === 0) return null;
    return (
      <div
        className={className ?? 'd-flex flex-wrap gap-1 align-items-center'}
        aria-label={ariaLabel ?? 'Event tags'}
      >
        {visible.map((tag) => (
          <span key={tag} className={chipClass}>{formatTag(tag)}</span>
        ))}
        {overflow > 0 && <span className="text-muted small">+{overflow}</span>}
      </div>
    );
  }

  return (
    <div
      className={className ?? 'd-flex flex-wrap gap-1 align-items-center position-relative'}
      aria-label={ariaLabel ?? 'Event tags'}
    >
      {visible.map((tag) => (
        <span key={tag} className={chipClass}>{formatTag(tag)}</span>
      ))}
      {overflow > 0 && <span className="text-muted small">+{overflow}</span>}
      <button
        ref={triggerRef}
        type="button"
        className="btn btn-link btn-sm p-0 ms-1 text-muted"
        onClick={(e) => {
          e.stopPropagation();
          setOpen((o) => !o);
        }}
        aria-label={`Edit tags for ${event.title}`}
        aria-expanded={open}
        aria-haspopup="dialog"
        title="Edit tags"
      >
        <i className="bi bi-tags" aria-hidden="true" />
        <span className="visually-hidden">Edit tags</span>
      </button>
      {open && (
        <TagEditorPopover
          ref={popoverRef}
          event={event}
          saving={saving}
          error={error}
          onAdd={addTag}
          onRemove={removeTag}
          onClose={() => setOpen(false)}
        />
      )}
    </div>
  );
}

interface PopoverProps {
  event: ExtractedEvent;
  saving: boolean;
  error: string | null;
  onAdd: (tag: string) => void;
  onRemove: (tag: string) => void;
  onClose: () => void;
}

const TagEditorPopover = forwardRef<HTMLDivElement, PopoverProps>(function TagEditorPopover(
  { event, saving, error, onAdd, onRemove, onClose },
  ref
) {
    const current = event.tags ?? [];
    const currentSet = useMemo(() => new Set(current), [current]);
    const [query, setQuery] = useState('');
    const inputRef = useRef<HTMLInputElement | null>(null);

    useEffect(() => {
      inputRef.current?.focus();
    }, []);

    const suggestions = useMemo(() => {
      const q = query.trim().toLowerCase();
      const pool = ALL_TAXONOMY_TAGS.filter((t) => !currentSet.has(t));
      if (!q) return pool.slice(0, 12);
      return pool.filter((t) => t.includes(q) || formatTag(t).toLowerCase().includes(q)).slice(0, 12);
    }, [query, currentSet]);

    const exactMatch = ALL_TAXONOMY_TAGS.includes(slugifyTag(query));
    const customSlug = slugifyTag(query);
    const showCustom = !!customSlug && !exactMatch && !currentSet.has(customSlug);

    const handleSubmit = (e: React.FormEvent) => {
      e.preventDefault();
      if (!query.trim()) return;
      onAdd(query);
      setQuery('');
    };

    return (
      <div
        ref={ref}
        className="card shadow position-absolute mh-tag-editor-popover"
        role="dialog"
        aria-label="Edit event tags"
        style={{ zIndex: 1050, top: '100%', left: 0, minWidth: 280, maxWidth: 360, marginTop: 4 }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="card-body p-2">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <strong className="small">Edit tags</strong>
            <button
              type="button"
              className="btn-close btn-close-sm"
              aria-label="Close tag editor"
              onClick={onClose}
            />
          </div>

          {current.length === 0 && (
            <p className="text-muted small mb-2">No tags yet.</p>
          )}
          {current.length > 0 && (
            <div className="d-flex flex-wrap gap-1 mb-2">
              {current.map((tag) => (
                <span key={tag} className="badge rounded-pill text-bg-light border small d-inline-flex align-items-center">
                  {tagIcon(tag) && <span className="me-1" aria-hidden="true">{tagIcon(tag)}</span>}
                  {formatTag(tag)}
                  <button
                    type="button"
                    className="btn btn-link p-0 ms-1 text-danger lh-1"
                    style={{ fontSize: '0.85rem' }}
                    aria-label={`Remove ${formatTag(tag)} tag`}
                    title={`Remove ${formatTag(tag)}`}
                    disabled={saving}
                    onClick={() => onRemove(tag)}
                  >
                    <i className="bi bi-x-circle-fill" aria-hidden="true" />
                  </button>
                </span>
              ))}
            </div>
          )}

          <form onSubmit={handleSubmit} className="mb-1">
            <label htmlFor="mh-tag-add" className="visually-hidden">Add tag</label>
            <div className="input-group input-group-sm">
              <input
                id="mh-tag-add"
                ref={inputRef}
                type="text"
                className="form-control"
                placeholder="Search or add tag…"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                disabled={saving}
                autoComplete="off"
                list="mh-tag-suggestions"
              />
              <button
                type="submit"
                className="btn btn-outline-primary"
                disabled={saving || !query.trim()}
              >
                Add
              </button>
            </div>
          </form>

          {(suggestions.length > 0 || showCustom) && (
            <div className="mt-2 d-flex flex-wrap gap-1" role="listbox" aria-label="Tag suggestions">
              {suggestions.map((tag) => (
                <button
                  key={tag}
                  type="button"
                  className="badge rounded-pill text-bg-light border small btn p-1"
                  disabled={saving}
                  onClick={() => { onAdd(tag); setQuery(''); }}
                  title={`Add ${formatTag(tag)}`}
                >
                  {tagIcon(tag) && <span className="me-1" aria-hidden="true">{tagIcon(tag)}</span>}
                  {formatTag(tag)}
                </button>
              ))}
              {showCustom && (
                <button
                  type="button"
                  className="badge rounded-pill text-bg-info text-dark small btn p-1"
                  disabled={saving}
                  onClick={() => { onAdd(query); setQuery(''); }}
                  title={`Add custom tag "${customSlug}"`}
                >
                  <i className="bi bi-plus-lg me-1" aria-hidden="true" />
                  {formatTag(customSlug)}
                </button>
              )}
            </div>
          )}

          {error && (
            <div className="alert alert-danger small py-1 px-2 mt-2 mb-0" role="alert">
              {error}
            </div>
          )}
          {saving && (
            <div className="text-muted small mt-2" aria-live="polite">Saving…</div>
          )}
        </div>
      </div>
    );
  }
);